import React from "react"
import { Drawer, Empty, Spin, Tag, Button, Tooltip } from "antd"
import { CalendarOutlined, ClockCircleOutlined, EnvironmentOutlined, LinkOutlined, LogoutOutlined } from "@ant-design/icons"
import moment from "moment"
import { translations, THEME_COLOR } from "../../utils/constants"

// Renders the user's Google Calendar events (already fetched by the parent)
// grouped by day inside a right-side drawer.
const GoogleCalendarEvents = ({ open, onClose, events = [], loading = false, googleUser, dateRange, language, onDisconnect }) => {
  const t = translations[language] || translations.en

  const isAllDay = (event) => !!event?.start?.date

  const startOf = (event) =>
    event?.start?.dateTime
      ? moment(event.start.dateTime)
      : moment(event?.start?.date, "YYYY-MM-DD")

  const endOf = (event) =>
    event?.end?.dateTime
      ? moment(event.end.dateTime)
      : moment(event?.end?.date, "YYYY-MM-DD")

  // Group events by calendar day so the list reads like an agenda.
  const grouped = events.reduce((acc, event) => {
    const start = startOf(event)
    if (!start.isValid()) return acc
    const dayKey = start.format("YYYY-MM-DD")
    if (!acc[dayKey]) acc[dayKey] = []
    acc[dayKey].push(event)
    return acc
  }, {})

  const sortedDays = Object.keys(grouped).sort()

  const timeLabel = (event) => {
    if (isAllDay(event)) return t?.allDay || "All day"
    const start = startOf(event)
    const end = endOf(event)
    if (!end.isValid()) return start.format("HH:mm")
    return `${start.format("HH:mm")} - ${end.format("HH:mm")}`
  }

  return (
    <Drawer
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <CalendarOutlined style={{ color: THEME_COLOR }} />
          <span>{t?.googleCalendarTitle || "Google Calendar"}</span>
        </div>
      }
      width={480}
      placement="right"
      onClose={onClose}
      open={open}
      destroyOnClose
    >
      {googleUser && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
            padding: "8px 12px",
            marginBottom: 16,
            borderRadius: 8,
            background: "#f6ffed",
            border: "1px solid #b7eb8f",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            {googleUser.photo && (
              <img
                src={googleUser.photo}
                alt=""
                style={{ width: 24, height: 24, borderRadius: "50%" }}
                referrerPolicy="no-referrer"
              />
            )}
            <span
              style={{
                fontSize: 13,
                color: "#333",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {t?.connectedAs || "Connected as"} <strong>{googleUser.email}</strong>
            </span>
          </div>
          <Tooltip title={t?.disconnectGoogle || "Disconnect"}>
            <Button
              size="small"
              type="text"
              icon={<LogoutOutlined />}
              onClick={onDisconnect}
            />
          </Tooltip>
        </div>
      )}

      {dateRange?.[0] && dateRange?.[1] && (
        <p style={{ fontSize: 12, color: "#999", marginBottom: 16 }}>
          {dateRange[0].format("DD/MM/YYYY")} → {dateRange[1].format("DD/MM/YYYY")}
        </p>
      )}

      {loading ? (
        <div style={{ textAlign: "center", padding: "48px 0" }}>
          <Spin size="large" />
        </div>
      ) : sortedDays.length === 0 ? (
        <Empty description={t?.noCalendarEvents || "No events found in the selected date range."} />
      ) : (
        sortedDays.map((dayKey) => (
          <div key={dayKey} style={{ marginBottom: 20 }}>
            <div
              style={{
                fontWeight: 600,
                fontSize: 14,
                marginBottom: 8,
                paddingBottom: 4,
                borderBottom: `2px solid ${THEME_COLOR}`,
              }}
            >
              {moment(dayKey, "YYYY-MM-DD").format("dddd, DD/MM/YYYY")}
            </div>

            {grouped[dayKey].map((event) => (
              <div
                key={event.id}
                style={{
                  padding: "10px 12px",
                  marginBottom: 8,
                  borderRadius: 8,
                  background: "#fafafa",
                  border: "1px solid #f0f0f0",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 8,
                  }}
                >
                  <strong style={{ fontSize: 13, wordBreak: "break-word" }}>
                    {event.summary || t?.noTitle || "(No title)"}
                  </strong>
                  {event.htmlLink && (
                    <Tooltip title={t?.openInGoogleCalendar || "Open in Google Calendar"}>
                      <a href={event.htmlLink} target="_blank" rel="noreferrer">
                        <LinkOutlined />
                      </a>
                    </Tooltip>
                  )}
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    marginTop: 6,
                    fontSize: 12,
                    color: "#555",
                  }}
                >
                  <ClockCircleOutlined />
                  <span>{timeLabel(event)}</span>
                  {isAllDay(event) && <Tag color="green">{t?.allDay || "All day"}</Tag>}
                </div>

                {event.location && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      marginTop: 4,
                      fontSize: 12,
                      color: "#777",
                    }}
                  >
                    <EnvironmentOutlined />
                    <span>{event.location}</span>
                  </div>
                )}

                {event.calendarName && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      marginTop: 4,
                      fontSize: 11,
                      color: "#999",
                    }}
                  >
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: event.calendarColor || "#999",
                        flexShrink: 0,
                      }}
                    />
                    <span>
                      {event.calendarName}
                      {event.calendarPrimary ? "" : ` (${t?.sharedCalendar || "shared"})`}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        ))
      )}
    </Drawer>
  )
}

export default GoogleCalendarEvents
